import { useEffect, useState } from 'react';

interface ColumnMapping {
  tableColumn: string;
  required: boolean;
  csvColumn: string | null;
  label: string;
  advanced: boolean;
}

export const useColumnMappingsForUpload = ({
  entity,
  templateColumns,
  csvColumns,
  resetKey,
  autoMap = false,
}: {
  entity: 'product' | 'transaction' | 'customer';
  templateColumns: Array<{
    name: string;
    required: boolean;
    label: string;
    advanced: boolean;
  }>;
  csvColumns: string[];
  resetKey?: boolean;
  autoMap?: boolean;
}) => {
  const STORAGE_KEY = `sales-tax-upload-column-mappings-${entity}`;

  const getInitialMappings = () => {
    const savedMappings = localStorage.getItem(STORAGE_KEY);
    if (!savedMappings) return null;

    const parsed = JSON.parse(savedMappings);

    return Object.fromEntries(
      Object.entries(parsed).filter(([_, value]) =>
        csvColumns.includes(value as string)
      )
    );
  };

  const [mappings, setMappings] = useState<ColumnMapping[]>([]);

  useEffect(() => {
    const savedMappings = getInitialMappings();

    const newMappings = templateColumns.map((column) => {
      let csvColumn = (savedMappings?.[column.label] as string) || null;

      // If no saved mapping exists and autoMap is enabled, try to find a match
      if (!csvColumn && autoMap) {
        const matchingColumn = csvColumns.find(
          (csvCol) => csvCol.toLowerCase() === column.label.toLowerCase()
        );
        if (matchingColumn) {
          csvColumn = matchingColumn;
        }
      }

      return {
        tableColumn: column.name,
        label: column.label,
        required: column.required,
        csvColumn,
        advanced: column.advanced,
      };
    });

    setMappings(newMappings);
  }, [resetKey, templateColumns, csvColumns, autoMap]);

  const handleColumnSelect = (
    tableColumn: string,
    csvColumn: string | null
  ) => {
    setMappings((prev) =>
      prev.map((mapping) =>
        mapping.tableColumn === tableColumn
          ? { ...mapping, csvColumn }
          : mapping
      )
    );
  };

  const saveMappings = (mappingObject: Record<string, string>) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mappingObject));
  };

  return {
    mappings,
    handleColumnSelect,
    saveMappings,
  };
};
